// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useLocation } from 'react-router-dom';
import { featureFlags } from '../../config/featureFlags';
import FundChatbot from '../funds/FundChatbot';
import { dispatchCompanionFundSelect } from './companionFundSelect';

/**
 * Routes where the global launcher must NOT render. Login + OAuth +
 * the public provider-access page have no advisor JWT in scope, so a
 * chat session can't be created anyway.
 */
const HIDDEN_ROUTE_PREFIXES = [
  '/login',
  '/oauth',
  '/oauth/callback',
  '/accept-invitation',
  '/registration-success',
  '/provider-access',
  '/awaiting-approval',
];

/**
 * Global Advisor Companion launcher. Mounted once at the App root, the
 * launcher renders the same dock UI as the legacy Fund Center
 * FundChatbot — but the session is created against the
 * `ADVISOR_COMPANION` use case, so the dock is available on every
 * page, not only `/funds`.
 *
 * Page-context-awareness (Phase 2 of the Advisor Companion PRD) and
 * the propose-confirm write surface (Phase 4) layer onto this same
 * mount point.
 *
 * Fund-link clicks dispatch a window CustomEvent that `FundsPage`
 * listens for to scroll-to-row. On any other route the event is a
 * no-op (the assistant typically only emits `fund:` links during a
 * Funds discussion).
 */
export function CompanionLauncher() {
  const location = useLocation();

  if (!featureFlags.enableAdvisorCompanion) return null;

  const pathname = location.pathname;
  for (const prefix of HIDDEN_ROUTE_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return null;
    }
  }

  return (
    <FundChatbot
      useCaseCode='ADVISOR_COMPANION'
      onSelectFund={dispatchCompanionFundSelect}
    />
  );
}

export default CompanionLauncher;
